import { useCallback, useMemo } from "react";
import {
  DMAutomationContent,
  DMAutomationContentType,
  DMAutomationPlatform,
  DMAutomationTrigger,
} from "@markit/common.types";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { VerticalDivider } from "../../../Dividers/VerticalDivider";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import CustomLinkify from "../../../Links/CustomLinkify";
import DMAutomationTriggerPreview from "../Items/DMAutomationTriggerPreview";

type DMAutomationFlowPanelProps = {
  dmAutomationTriggers: DMAutomationTrigger[];
  dmAutomationContents: DMAutomationContent[];
  platform: DMAutomationPlatform;
  onEditItem: (isContent: boolean) => void;
};

const DMAutomationFlowPanel = (props: DMAutomationFlowPanelProps) => {
  const { dmAutomationTriggers, dmAutomationContents, platform, onEditItem } =
    props;

  // Only display the DM_Message content in the flow
  const dmMessageContents = useMemo(
    () =>
      dmAutomationContents.filter(
        (content) => content.type === DMAutomationContentType.DM_MESSAGE
      ),
    [dmAutomationContents]
  );

  const styles = {
    itemContainer: {
      paddingBlock: 14,
      backgroundColor: Colors.WHITE,
      borderRadius: 14,
      width: 299,
      cursor: "pointer",
    },
  };

  const renderVerticalLine = (
    <div style={{ paddingBlock: 4 }}>
      <VerticalDivider height={30} color={Colors.GRAY2} />
    </div>
  );

  const renderContentItem = useCallback(
    (content: DMAutomationContent) => (
      <div key={content.id} style={{ borderRadius: 18, padding: 4 }}>
        <StandardBorderedContainer
          containerStyles={styles.itemContainer}
          onPress={() => onEditItem(true)}
        >
          <div className="AlignedRow" style={{ gap: 7, paddingInline: 14 }}>
            <Icon icon={"ion:chatbubble-ellipses"} height={15} />
            <span className="bodyMedium">Send a DM</span>
          </div>
          <HorizontalDivider altMargin={14} />
          <div className="ColumnNormal" style={{ paddingInline: 14, gap: 10 }}>
            <CustomLinkify>
              <span className="TwoLineText smallBodySubtext">
                {content.id
                  ? content.message
                  : "Add a new message to your automation"}
              </span>
            </CustomLinkify>
            {content.id
              ? content.buttons.map((button) => (
                  <StandardBorderedContainer
                    key={button.id}
                    containerStyles={{
                      paddingInline: 14,
                      paddingBlock: 10,
                      backgroundColor: Colors.WHITE1,
                    }}
                  >
                    <div className="AlignedRowSpaced" style={{ gap: 14 }}>
                      <span className="bodySmallMedium OneLineText">
                        {button.title}
                      </span>
                      <div>
                        <Icon
                          icon={"mdi:link-variant"}
                          height={16}
                          style={{ color: Colors.BLUE5 }}
                        />
                      </div>
                    </div>
                  </StandardBorderedContainer>
                ))
              : null}
          </div>
        </StandardBorderedContainer>
      </div>
    ),
    [onEditItem, styles.itemContainer]
  );

  return (
    <div
      className="ColumnNormal AllowScroll"
      style={{
        paddingBlock: 40,
        paddingInline: 40,
        alignItems: "center",
        backgroundColor: Colors.WHITE1,
      }}
    >
      <div style={{ borderRadius: 18, padding: 4 }}>
        <StandardBorderedContainer
          containerStyles={{ ...styles.itemContainer }}
        >
          <div className="AlignedRow" style={{ gap: 7, paddingInline: 14 }}>
            <Icon icon={"ion:flash"} height={15} />
            <span className="bodyMedium">Trigger</span>
          </div>
          <HorizontalDivider altMargin={14} />
          <div className="ColumnNormal" style={{ gap: 14 }}>
            {dmAutomationTriggers.slice(0, 3).map((trigger) => {
              return (
                <div
                  key={trigger.id}
                  style={{ paddingInline: 14, cursor: "pointer" }}
                  onClick={() => onEditItem(false)}
                >
                  <DMAutomationTriggerPreview
                    trigger={trigger}
                    platform={platform}
                    iconSize={34}
                  />
                </div>
              );
            })}
            {dmAutomationTriggers.length > 3 ? (
              <div className="ColumnCentering">
                <span className="smallBodySubtext">
                  and {dmAutomationTriggers.length - 3} more...
                </span>
              </div>
            ) : null}
          </div>
        </StandardBorderedContainer>
      </div>
      {renderVerticalLine}
      {dmMessageContents.map((content) => (
        <div
          key={content.id}
          className="ColumnNormal"
          style={{ alignItems: "center" }}
        >
          {renderContentItem(content)}
        </div>
      ))}
    </div>
  );
};

export default DMAutomationFlowPanel;
